import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Table } from "src/components";
import Loader from "src/components/ui/loader.component";
import StockistDetailsGrid from "src/container/details-grid/stockistDetails-grid.component";
import TableActiveInactiveDropdown from "src/container/table-active-inactive-dropdown.component";
import { stockistActions } from "src/context/stockist-slice";
import { RootState } from "src/context/store";
import { useToast } from "src/hooks/useToast";
import { editStockistCompany, queryStockist, verifyInactive } from "src/services/stockist-api";
import DeleteModal from "src/container/DeleteModal";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import usePermission from "src/hooks/usePermission";
import OrderModalComponent from "src/container/OrderModalComponent";

const columnHelper = createColumnHelper();

interface Data {
  company: any;
  company_name: any;
  operator_name: any;
  operator_mobile: any;
  operator_email: any;
  plantCode: any;
  remarks?: any;
  reference_number: any;
  compStockistCode: any;
  compStockistAliasCode: any;
  stockist_type: any;
  stockist: any;
  elnv_exempt: any;
  sao_block_exempt: any;
  stockist_default: any;
  ewaybill_applicable: any;
  stk_dl_number: any;
  module_codes: any;
  stockistClusterLnk: any[];
  id: any;
  active_status?: "Y" | "N"; // Optional property to handle active status
  owner_name: any;
  city: any;
  email: any;
  medvol_code: any;
  firm_name: any;
  nonmv_order_applicable: string;
}

const DetailsSection = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(true);
  // const [activeStatus, setactiveStatus] = useState<boolean>();
  const { selectedStockistData } = useSelector((state: RootState) => state.stockist);
  const [companyToggleData, setCompanyToggleData] = useState<{ [key: string]: any } | null>(null);
  const [showRefNumber, setShowRefNumber] = useState<any>(false);

  const [selectedOrder, setSelectedOrder] = useState<any>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { toast } = useToast();

  const { hasPermission: hasPermissionCompanyEditActiveInactive } = usePermission({
    module: fineGrainedPermissionsKeys.STOCKIST["EDIT/ACTIVE/INACTIVECOMPANY"].module,
    action: fineGrainedPermissionsKeys.STOCKIST["EDIT/ACTIVE/INACTIVECOMPANY"].action,
  });

  const fetchDetails = useCallback(async () => {
    if (!window.sessionStorage.getItem("stockistMasterId")) {
      navigate("/masters/stockists");
    }

    const queryParams = `?stockist_id=${window.sessionStorage.getItem("stockistMasterId")}`;
    setIsDataLoaded(false);
    let response: any = null;
    response = await queryStockist(`/${queryParams.toString()}&company=true&cluster=true&perPage=0&page=0`);
    if (response?.status !== 200) throw new Error();
    // response = await queryStockist(`/${queryParams.toString()}&company=true&cluster=true`);

    setIsLoading(false);
    dispatch(stockistActions.setSelectedStockistData(response?.data?.data?.data[0]));
    setIsDataLoaded(true);
  }, [dispatch]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (!selectedStockistData || Object.keys(selectedStockistData)?.length === 0) {
      setIsLoading(true);
      interval = setInterval(async () => {
        try {
          await fetchDetails();
        } catch (error) {
          clearInterval(interval);
          setIsLoading(false);
          toast({
            description: "Unable to fetch details",
            variant: "destructive",
          });
        }
      }, 1500);
    }
    return () => {
      clearInterval(interval);
    };
  }, [fetchDetails, selectedStockistData]);

  const transformRowData = (data: any) => {
    const rowData = structuredClone(data);
    const activeClusterLnk = rowData.stockistClusterLnk?.filter((item: any) => item?.active_status !== "N") || [];  //M10-14028
    const tempObj: any = {};
    activeClusterLnk.forEach((item: any, index: number) => {  //M10-14028
      (tempObj[`clusterCode__${index}`] = item?.Cluster_Code ?? ""),
        (tempObj[`margin__${index}`] = item.paymentTermHdr ?? item.margin ?? ""),
        (tempObj[`paymentTermHdr__${index}`] = item.paymentTermHdr ?? ""),
        (tempObj[`distributionChannel__${index}`] = item.distributionChannel),
        (tempObj[`districtsCovered__${index}`] = Array.isArray(item.stockistcluster_link_id)
          ? item.stockistcluster_link_id.map((link: any) => ({
            ...link,
            ...(link?.region_code || {}),
          }))
          : []);
    });

    return {
      ...rowData,
      ...tempObj,
      elnv_exempt: { elnv_exempt: rowData?.elnv_exempt, id: 1 },
      stockist_type: { stockist_type: rowData?.stockist_type, id: 1 },
    };
  };

  const handleEditCompany = async (data: any) => {
    const activeClusterCount = data?.stockistClusterLnk?.filter((item: any) => item?.active_status !== "N")?.length || 1; //M10-14028
    dispatch(stockistActions.setClusterFormSectionCounter(activeClusterCount)); //M10-14028
    dispatch(stockistActions.setStockistLinkFormFullObject(transformRowData(data)));
    dispatch(stockistActions.linkClusterCompanyFormData({ status: true }));
    dispatch(stockistActions.setMainFormEditMode(true));
    dispatch(stockistActions.setStockistLinkEditMode(true));
  };

  const handleLinkCompany = async (referenceDetails: any) => {
    const modulePayload = companyToggleData?.modules?.map((item: any) => ({
      module_code: item?.module?.module_code,
      active_status: item?.active_status,
      stkcmp_module_id: item?.stkcmp_module_id,
    }));

    const data: Data = {
      company: companyToggleData?.company?.code, // Assuming company is represented by an ID
      company_name: companyToggleData?.company?.name,
      operator_name: companyToggleData?.operator_name,
      operator_mobile: companyToggleData?.operator_mobile,
      operator_email: companyToggleData?.operator_email,
      plantCode: companyToggleData?.plantCode,
      compStockistCode: companyToggleData?.compStockistCode,
      compStockistAliasCode: companyToggleData?.compStockistAliasCode,
      stockist_type: companyToggleData?.stockist_type || null,
      stockist_default: companyToggleData?.stockist_default || null,
      ewaybill_applicable: companyToggleData?.ewaybill_applicable || "N",
      stk_dl_number: companyToggleData?.stk_dl_number || null,
      stockist: window.sessionStorage.getItem("stockistMasterId"),
      elnv_exempt: companyToggleData?.elnv_exempt,
      sao_block_exempt: companyToggleData?.sao_block_exempt,
      module_codes: modulePayload || null,
      nonmv_order_applicable: companyToggleData?.nonmv_order_applicable,
      stockistClusterLnk: companyToggleData?.stockistClusterLnk?.map((item: any) => {
        return {
          id: item?.id,
          active_status: item?.active_status,
          clusterCode: item?.Cluster_Code?.cluster_code,
          cluster_name: item?.Cluster_Code?.cluster_name,
          paymentTermHdr: item?.paymentTermHdr?.id,
          payment_paymentTermCode: item?.paymentTermHdr?.paymentTermCode,
          margin: item?.paymentTermHdr?.margin,
          distributionChannel: item?.distributionChannel,
          stockist_regioncovered:
            item?.stockistcluster_link_id?.map((region: any) => ({
              region_type: region?.region_type,
              region_code: region?.region_code?.district_code || null,
              active_status: "Y",
              stockistcluster_link_id: region?.stockistcluster_link_id || null,
              stockist_regioncovered_id: region?.stockist_regioncovered_id || null,
            })) || [],
        };
      }),
      id: companyToggleData?.id || null,
      owner_name: selectedStockistData?.owner_name,
      firm_name: selectedStockistData?.firm_name,
      city: selectedStockistData?.city?.city_code,
      email: selectedStockistData?.email,
      medvol_code: selectedStockistData?.medvol_code,
      reference_number: referenceDetails?.reference_number,
    };

    data["active_status"] = companyToggleData?.active_status != "Y" ? "Y" : "N";

    try {
      const resCompany = await editStockistCompany({ ...data, stockist: data?.stockist }, data?.stockist);

      if (resCompany?.status === 200) {
        toast({
          description: "Company status updated successfully",
        });
        dispatch(stockistActions.setShowStockistMainForm(false));
      }
    } catch (error) {
      console.log(error);
    } finally {
      setCompanyToggleData(null);
      setShowRefNumber(false);
    }

    dispatch(stockistActions.setLinkedCompaniesData(data));
    setTimeout(() => {
      fetchDetails();
    }, 1500);
  };

  // Define the columns for the table
  const columns = useMemo(
    () => [
      columnHelper.accessor("company", {
        cell: (info: any) => (
          <div className="px-4 py-3 overflow-hidden text-sm not-italic font-semibold text-ellipsis line-height-normal whitespace-nowrap">
            {info.getValue()?.shortname}
          </div>
        ),
        header: () => "Company",
        enableSorting: true,
      }),
      columnHelper.accessor("active_status", {
        header: () => "Link Status",
        cell: (info: any) => (
          <div className="px-4 py-3 overflow-hidden text-sm not-italic font-semibold text-ellipsis line-height-normal whitespace-nowrap">
            <TableActiveInactiveDropdown
              disabled={!isDataLoaded || !hasPermissionCompanyEditActiveInactive}
              defaultValue={info.getValue() === "Y" ? "active" : "inactive"}
              onChanged={() => handleCompanyChange(info)}
              className="disabled:opacity-100"
            />
          </div>
        ),
        enableSorting: true,
      }),
      columnHelper.accessor("action", {
        header: () => "Action",
        cell: (info: any) => (
          <Button
            onClick={() => {
              handleEditCompany(info?.row?.original);
            }}
            className={`${info?.row?.original?.active_status === "N" ? "text-gray-400 cursor-not-allowed" : "text-blue-600 underline"}`}
            disabled={info?.row?.original?.active_status === "N"}
            module={fineGrainedPermissionsKeys.STOCKIST["EDIT/ACTIVE/INACTIVECOMPANY"].module}
            action={fineGrainedPermissionsKeys.STOCKIST["EDIT/ACTIVE/INACTIVECOMPANY"].action}
          >
            Edit
          </Button>
        ),
        enableSorting: true,
      }),
    ],
    [isDataLoaded, hasPermissionCompanyEditActiveInactive]
  );

  function switchString(input: string) {
    switch (input.toLowerCase()) {
      case "stockist_id":
        return undefined;
      case "owner_name":
        return "Owner Name";
      case "owner_mobile_no":
        return "Owner Mobile Number";
      case "medvol_code":
        return "Medvol Code";
      case "uid":
        return undefined;
      case "cognito_id":
        return "Cognito ID";
      case "created_by":
        return undefined;
      case "created_date":
        return "Created Date";
      case "modified_by":
        return undefined;
      case "modified_date":
        return "Modified Date";
      case "active_status":
        return undefined;
      case "firm_name":
        return "Firm Name";
      case "manager_name":
        return "Manager Name";
      case "manager_mobile_no":
        return "Manager Mobile Number";
      case "address":
        return "Address";
      case "gst":
        return "GST";
      case "drug_licence":
        return "Drug Licence";
      case "food_licence":
        return "Food Licence";
      case "email":
        return "Email";
      case "u_code":
        return undefined;
      case "group_name":
        return "Group Name";
      case "elnv_qualification":
        return "E-lnv. Qualification";
      case "elnv_invoice":
        return "E-Invoice Ack.";
      case "medvol_field":
        return "Medvol Field";
      case "gst_available":
        return "GST Available";
      case "gst_status":
        return "GST Status";
      case "city_name":
        return "City Name";
      case "einvack_days":
        return "E-Inv Ack Days";
      case "chain_pharmacy":
        return "Chain Stockist";
      default:
        return input;
    }
  }

  const handleCompanyChange = async (info: any) => {
    try {
      const companyCode = info?.row?.original?.company?.code;
      const stockistAlias = info?.row?.original?.compStockistCode;
      const originalData = info?.row?.original;

      if (!companyCode || !stockistAlias) {
        console.warn('Missing companyCode or stockistAlias:', { companyCode, stockistAlias });
        return;
      }

      const response = await verifyInactive(companyCode, stockistAlias);
      const resultData = response?.data?.data || [];

      if (resultData.length === 0) {
        setCompanyToggleData(originalData);
        setShowRefNumber(true);
        return;
      }

      setSelectedOrder(resultData);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error verifying inactive company:', error);
    }
  };

  const handleCloseModal = () => {
    setSelectedOrder(null);
    setIsModalOpen(false);
  };

  if (isLoading) return <Loader />;

  return (
    <section className="h-auto min-h-screen pb-20 ">
      <div className="display-f">
        <div className="flex-1">
          <div className="flex flex-col mb-2">
            <h3 className="text-xl font-semibold line-height-normal leading-[0.025rem] flex gap-2 items-center">User Details</h3>
            <p className="mt-2 text-xs font-semibold text-gray-500 ">View basic user details and information</p>
          </div>

          <Divider />

          <StockistDetailsGrid removeNestedData={true} dataObj={selectedStockistData} keyString={switchString} />

          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-xl font-semibold line-height-normal leading-[0.025rem]  mt-14 ">Company Associations</h3>
              <p className="my-2 text-xs font-semibold text-gray-500">View basic user details and information</p>
            </div>
            {selectedStockistData?.active_status === "Y" && (
              <Button
                className="border"
                onClickCapture={() => {
                  dispatch(stockistActions.setStockistLinkFormFullObject({}));
                  dispatch(stockistActions.linkClusterCompanyFormData({ status: true }));
                  dispatch(stockistActions.setMainFormEditMode(false));
                  dispatch(stockistActions.setIndependentAddCompany(true));
                }}
                module={fineGrainedPermissionsKeys.STOCKIST.ADDCOMPANY.module}
                action={fineGrainedPermissionsKeys.STOCKIST.ADDCOMPANY.action}
              >
                Add Company
              </Button>
            )}
          </div>
          <Divider />

          <Table data={selectedStockistData?.stockist_company_lnk?.length > 0 ? selectedStockistData?.stockist_company_lnk : []} columns={columns} />
        </div>
      </div>
      <OrderModalComponent
        order={selectedOrder}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
      <DeleteModal value={showRefNumber} setValue={setShowRefNumber} onFormSubmit={handleLinkCompany} />
    </section>
  );
};

const Divider = () => <div className="h-[0.0625rem] w-full bg-[#00000010] mx-auto my-3" />;

export default DetailsSection;
